import { cn } from '@amore/ui/cn'
import { Checkbox } from '@amore/ui/components/checkbox'
import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Input } from '@amore/ui/components/input'
import { Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import { type Venue, venueIdentity } from '@/mock/venues'
import { ConfirmDialog, DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { normalize, sameVenueDraft, type VenueEditSection, venueOperationFields } from './model'
import { VenueAgreement, VenueTextFields, VenueUses } from './venue-fields'
import { VenueMaterials } from './venue-materials'

const titles: Record<VenueEditSection, string> = {
  identity: 'Údaje místa',
  agreement: 'Domluva s místem',
  operations: 'Příjezd a zázemí',
  materials: 'Soubory a poznámky',
}

export function PlaceSheet({
  venue,
  section,
  onClose,
  onRemoved,
}: {
  venue: Venue
  section: VenueEditSection
  onClose: () => void
  onRemoved: () => void
}) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const [draft, setDraft] = useState(venue)
  const [customUse, setCustomUse] = useState('')
  const [error, setError] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const nameRef = useRef<HTMLTextAreaElement>(null)
  const identity = venueIdentity(draft, data)
  const source = draft.source
  const dirty = canEdit && (!sameVenueDraft(draft, venue) || Boolean(customUse.trim()))
  const dateError =
    draft.availableFrom && draft.availableTo && draft.availableTo < draft.availableFrom
      ? 'Konec dostupnosti musí být stejný nebo pozdější než začátek.'
      : undefined
  const patch = (changes: Partial<Venue>) => {
    setError('')
    setDraft((current) => ({ ...current, ...changes }))
  }
  const save = () => {
    if (!canEdit) return
    if (!identity.name.trim()) {
      setError('Doplňte název místa.')
      nameRef.current?.focus()
      return
    }
    if (
      data.venues.some(
        (item) =>
          item.id !== venue.id &&
          normalize(venueIdentity(item, data).name) === normalize(identity.name),
      )
    ) {
      setError('Místo s tímto názvem už máte v plánu.')
      nameRef.current?.focus()
      return
    }
    if (dateError) {
      setError(dateError)
      return
    }
    const saved: Venue = {
      ...draft,
      uses:
        customUse.trim() && !draft.uses.some((use) => normalize(use) === normalize(customUse))
          ? [...draft.uses, customUse.trim()]
          : draft.uses,
      source:
        source.type === 'custom'
          ? { ...source, name: source.name.trim(), address: source.address.trim() }
          : source,
    }
    update((current) => ({
      ...current,
      venues: current.venues.map((item) => (item.id === venue.id ? saved : item)),
    }))
    showToast({ title: 'Místo uloženo', description: identity.name, tone: 'success' })
    onClose()
  }
  const remove = () => {
    if (!canEdit) return
    update((current) => ({
      ...current,
      venues: current.venues.filter((item) => item.id !== venue.id),
    }))
    showToast({
      title: 'Místo odebráno',
      description: identity.name,
      action: {
        label: 'Vrátit zpět',
        altText: 'Vrátit odebrané místo do plánu',
        onClick: () =>
          update((current) => ({
            ...current,
            venues: current.venues.some((item) => item.id === venue.id)
              ? current.venues
              : [...current.venues, venue],
          })),
      },
    })
    onRemoved()
  }
  return (
    <>
      <DetailSheet
        open
        eyebrow={section === 'identity' ? 'Místo' : identity.name}
        title={section === 'identity' ? identity.name : titles[section]}
        dirty={dirty}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        titleContent={
          section === 'identity' && source.type === 'custom' ? (
            <textarea
              ref={nameRef}
              value={source.name}
              rows={1}
              maxLength={160}
              readOnly={!canEdit}
              aria-label="Název místa"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'venue-edit-error' : undefined}
              placeholder="Název místa"
              onChange={(event) => patch({ source: { ...source, name: event.target.value } })}
              className={cn(
                'field-sizing-content -mx-2 w-[calc(100%+1rem)] resize-none rounded-lg bg-transparent px-2 py-1 font-heading text-[1.75rem] leading-tight tracking-tight outline-none transition-colors',
                canEdit && 'hover:bg-muted/60 focus:bg-muted/60',
              )}
            />
          ) : undefined
        }
        primaryAction={
          canEdit ? { label: 'Uložit', onClick: () => formRef.current?.requestSubmit() } : undefined
        }
        destructiveAction={
          canEdit && section === 'identity'
            ? {
                label: 'Odebrat místo',
                icon: <Trash2 className="size-4" aria-hidden="true" />,
                onClick: () => setConfirmRemove(true),
              }
            : undefined
        }
      >
        <FormLayout
          ref={formRef}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
          className="divide-y divide-border space-y-0 [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1"
        >
          {error ? (
            <p id="venue-edit-error" role="alert" className="text-sm text-error-foreground">
              {error}
            </p>
          ) : null}
          {section === 'identity' ? (
            <>
              {source.type === 'custom' ? (
                <fieldset disabled={!canEdit} className="space-y-5">
                  <legend className="sr-only">Údaje místa</legend>
                  <Field label="Adresa">
                    <Input
                      variant="soft"
                      className="h-10 border-transparent bg-muted/60 hover:bg-muted"
                      value={source.address}
                      maxLength={300}
                      placeholder="Ulice, číslo a obec"
                      onChange={(event) =>
                        patch({ source: { ...source, address: event.target.value } })
                      }
                    />
                  </Field>
                  <Checkbox
                    checked={source.private}
                    onChange={(event) =>
                      patch({ source: { ...source, private: event.target.checked } })
                    }
                  >
                    Soukromé místo
                  </Checkbox>
                </fieldset>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {identity.address ? `${identity.address}.` : 'Adresa zatím není uvedená.'} Název a
                  adresu spravujete u dodavatele.
                </p>
              )}
              <VenueUses
                value={draft.uses}
                onChange={(uses) => patch({ uses })}
                canEdit={canEdit}
                custom={customUse}
                onCustomChange={setCustomUse}
              />
            </>
          ) : null}
          {section === 'agreement' ? (
            <VenueAgreement draft={draft} patch={patch} canEdit={canEdit} dateError={dateError} />
          ) : null}
          {section === 'operations' ? (
            <VenueTextFields
              draft={draft}
              patch={patch}
              canEdit={canEdit}
              fields={venueOperationFields.filter(
                (field) => field.key !== 'accommodation' || draft.uses.includes('Ubytování'),
              )}
            />
          ) : null}
          {section === 'materials' ? (
            <>
              <VenueMaterials
                documents={draft.documents}
                canEdit={canEdit}
                onChange={(documents) => patch({ documents })}
              />
              <VenueTextFields
                draft={draft}
                patch={patch}
                canEdit={canEdit}
                fields={[
                  {
                    key: 'notes',
                    label: 'Poznámky',
                    placeholder: 'Co je důležité vědět a na čem jste se domluvili…',
                  },
                ]}
              />
            </>
          ) : null}
        </FormLayout>
      </DetailSheet>
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        heading={`Odebrat místo „${identity.name}“?`}
        description="Odeberete plán a podklady tohoto místa. Dodavatel a položky v rozpočtu zůstanou zachované."
        keepLabel="Ponechat místo"
        confirmLabel="Odebrat místo"
        onConfirm={remove}
      />
    </>
  )
}
